import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import * as FileSaver from 'file-saver';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

// CONFIGS
// COMPONENTS
// INTERFACSS
import { EProductTypes } from '../../../rfq/interfaces/rfq.interface';
import { trackByValue } from '../../../shared/util/track-by.util';
import { IStockOptions } from '../../interfaces/stock.interface';

// SERVICES
// UTILS
import { DialogConfig } from './../../../shared/configs/dialog.config';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';
import { IPagination } from './../../../shared/interfaces/paginator.interface';
import { StockFacadeService } from './../../../shared/services/facades/stock/stock.facade.service';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

@Component({
  selector: 'lum-stock-actions',
  templateUrl: './stock-actions.component.html',
  styleUrls: ['./stock-actions.component.scss'],
})
export class StockActionsComponent implements OnInit {
  warehouseOptions :IStockOptions[] = []; // Component level logic
  private onDestroy$ = new Subject();

  trackByValue = trackByValue;

  constructor(
    private stockFacadeService :StockFacadeService,
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.stockFacadeService.setLocationWarehouses([]);
    this.subscribeToWarehouses().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onConfirmResyncPending() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Resync Pending',
          message:
            'By clicking YES, Luminous will look at all currently open orders and will run a full pending resync across all SKUs. This action cannot be undone.',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.resyncPending();
        }
      });
  }

  onConfirmResetBaseline() :void {
    if (!this.warehousesSelectedIds.length) {
      this.snackBar.open('Please select at least one warehouse', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Reset Baseline',
          message:
            'Selecting this option will set the On Hand quantities for ALL Skus within the selected warehouse(s) to zero.',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.resetBaseline();
        }

        // Reset checkboxes
        // Component level logic
        this.warehouseOptions.forEach((warehouse) => {
          warehouse.checked = false;
        });
      });
  }
  onExportInventoryNumbers() :void {
    this.snackBar.open('Exporting Inventory Stocks - RFQ, please wait...', 'Close');
    this.getSkusPaginated({
      type: EProductTypes.SKU,
    });
  }

  onExportInventoryKitNumber() :void {
    this.snackBar.open('Exporting Inventory Stocks - Kit, please wait...', 'Close');
    this.getSkusPaginated({
      type: EProductTypes.KIT,
    });
  }
  private getSkusPaginated(data :{ type :any }) :void {
    const { type } = data;
    const params = {
      ...this.currentQueryParams,
      limit: this.pagination.total,
      exportType: 'xlsx',
    };
    if (type === EProductTypes.KIT) {
      params.productType = EProductTypes.KIT;
    }
    this.stockFacadeService.inventoryExport(params).subscribe({
      next: (response :any) => {
        FileSaver.saveAs(response, `${type}_Inventory_Stocks.xlsx`);
        if (response) {
          this.snackBar.dismiss();
          this.snackBar.open(`Exported Inventory Stocks - ${type}`, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      },
      error: (error) => {
        this.snackBar.dismiss();
        this.snackBar.open(
          error?.error?.message || 'Failed to export inventory stocks',
          'Fail',
        );
      },
    });
  }

  private resyncPending() :void {
    this.snackBar.open(
      'Processing Request. This may take some time, please wait...',
      'Success',
    );
    this.stockFacadeService
      .inventoryRecountPending()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.dismiss();
          this.snackBar.open(
            response?.success || 'Pending quantities are now updated.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (error) => {
          this.snackBar.dismiss();
          this.snackBar.open(
            error?.error?.message || 'Failed to resync pending. Try again later.',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  resetBaseline() :void {
    this.stockFacadeService
      .rfqResetBaselineToZero(this.warehousesSelectedIds)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.open(
            response?.success || 'Reset Baseline to Zero',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (error) => {
          this.snackBar.open(
            error?.error?.message || 'Failed to reset baseline to zero',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private subscribeToWarehouses() :Observable<any> {
    return this.stockFacadeService.locationWarehouses$.pipe(
      takeUntil(this.onDestroy$),
      tap(() => {
        this.setWarehouseOptions();
      }),
    );
  }

  private setWarehouseOptions() :void {
    this.warehouseOptions = this.warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.locationName,
      checked: false,
    }));
  }

  get warehouses() :any[] {
    return this.stockFacadeService.locationWarehouses;
  }

  get warehousesSelectedIds() :number[] {
    return this.warehouseOptions
      .filter((warehouse) => warehouse.checked)
      .map((warehouse) => warehouse.value);
  }
  get currentQueryParams() :any {
    return this.stockFacadeService.currentQueryParams;
  }

  get canExportUnitCost() :boolean {
    return this.stockFacadeService.canExportUnitCost;
  }
  get pagination() :IPagination {
    return this.stockFacadeService.stocksMeta;
  }
}
